import { Node, mergeAttributes } from '@tiptap/core';
import { ReactNodeViewRenderer, NodeViewWrapper } from '@tiptap/react';
import { NodeSelection } from '@tiptap/pm/state';
import React, { useState, useRef, useEffect } from 'react';
import { MAX_CONTENT_WIDTH, MAX_CONTENT_HEIGHT } from '../constants';
import { isCanonicalAsset, resolveSignedAsset, invalidateSignedAsset, getCachedSignedAsset } from '../../../lib/signedAssets';

// React component for resizable image
const ResizableImageComponent = ({ node, updateAttributes, selected, editor, getPos }) => {
  const [isResizing, setIsResizing] = useState(false);
  const [resizeDirection, setResizeDirection] = useState(null);
  // Position of the image the properties panel is currently bound to. When it
  // matches this node, keep the selection chrome (border + handles) visible even
  // if the ProseMirror NodeSelection was lost (e.g. focus moved to a panel input).
  const [panelBoundPos, setPanelBoundPos] = useState(null);
  // Layout size of the (unrotated) image, used to reserve the rotated bounding
  // box so a rotated image reflows the text instead of overlapping behind it.
  const [imgBox, setImgBox] = useState({ w: 0, h: 0 });
  const imageRef = useRef(null);
  const containerRef = useRef(null);
  const startPosRef = useRef({ x: 0, y: 0 });
  const startDimensionsRef = useRef({ width: 0, height: 0 });
  const textAlign = node.attrs.textAlign || 'left';
  const wrapMode = node.attrs.wrap || 'inline';

  // Canonical /assets paths must be resolved to a short-lived signed URL before
  // the browser can load them. External URLs / data URLs render as-is.
  const rawSrc = node.attrs.src;
  // Seed from the sync cache so a remount (switching sheets) paints immediately
  // when the signature is still fresh — avoids the null flash that shows the
  // dark editor paper through the reserved <img> box and bumps the inline image.
  const [displaySrc, setDisplaySrc] = useState(
    isCanonicalAsset(rawSrc) ? getCachedSignedAsset(rawSrc) : rawSrc
  );
  const reResolvedRef = useRef(false);

  useEffect(() => {
    let active = true;
    reResolvedRef.current = false;
    if (isCanonicalAsset(rawSrc)) {
      // Only clear when we have no fresh cached URL; otherwise keep the seeded
      // src visible instead of flashing the loading box.
      if (!getCachedSignedAsset(rawSrc)) setDisplaySrc(null);
      resolveSignedAsset(rawSrc).then((url) => {
        if (active) setDisplaySrc(url || rawSrc);
      });
    } else {
      setDisplaySrc(rawSrc);
    }
    return () => { active = false; };
  }, [rawSrc]);

  // While the <img> has a src but hasn't painted yet, back it with a light box
  // so the dark editor paper (#1e1e1e) doesn't show through as a black rectangle.
  // Cleared on load so transparent PNGs keep their transparency afterwards.
  const [imgPainted, setImgPainted] = useState(false);
  useEffect(() => { setImgPainted(false); }, [displaySrc]);

  // On load error for a signed asset, re-sign once (the URL may have expired).
  const handleImgError = () => {
    if (isCanonicalAsset(rawSrc) && !reResolvedRef.current) {
      reResolvedRef.current = true;
      invalidateSignedAsset(rawSrc);
      resolveSignedAsset(rawSrc).then((url) => {
        if (url) setDisplaySrc(url);
      });
    }
  };

  const startResize = (e, direction) => {
    setIsResizing(true);
    setResizeDirection(direction);
    // Signal the EditorPage selection poll that a resize is in progress so it
    // does NOT tear down the properties panel while the drag transiently drops
    // the NodeSelection (T-042.img).
    if (editor) editor.__imageResizing = true;
    // Kill native text selection during the drag. A real mouse drag over the
    // paragraph creates a DOM text selection that ProseMirror syncs back on
    // mouseup, overriding our NodeSelection → the image "deselects" and the panel
    // can't reopen. user-select:none stops that selection from ever forming.
    document.body.style.userSelect = 'none';
    document.body.style.webkitUserSelect = 'none';
    // The editable itself is contentEditable, so user-select:none on body may not
    // cascade — set it directly on the ProseMirror DOM too.
    if (editor?.view?.dom) {
      editor.view.dom.style.userSelect = 'none';
      editor.view.dom.style.webkitUserSelect = 'none';
    }
    startPosRef.current = { x: e.clientX, y: e.clientY };
    // Read the rendered size from the DOM — always fresh, unlike node.attrs
    // which may be null before the first resize.
    const rect = imageRef.current?.getBoundingClientRect();
    startDimensionsRef.current = {
      width: Math.round(rect?.width) || node.attrs.width || 300,
      height: Math.round(rect?.height) || node.attrs.height || 200,
    };
  };

  // T-042.img fix (attempt 5): ProseMirror registers a NATIVE mousedown handler
  // on .ProseMirror (an ancestor of these handles). A React onMouseDown is
  // delegated at the app root, so it fires AFTER PM's bubble handler has already
  // moved the selection off this atom node → `selected` flips false → the handles
  // unmount mid-drag → the image "deselects". Intercept in the CAPTURE phase on
  // the container so we beat PM's bubble handler and stopPropagation before it
  // ever sees the event. Only handle clicks: plain image clicks fall through so
  // PM still selects the node normally.
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const onDown = (e) => {
      const handle = e.target.closest?.('[data-resize-dir]');
      if (handle && el.contains(handle)) {
        e.preventDefault();
        e.stopPropagation();
        startResize(e, handle.dataset.resizeDir);
        return;
      }
    };
    el.addEventListener('mousedown', onDown, true);
    return () => el.removeEventListener('mousedown', onDown, true);
  }, []);

  // Listen for which image the properties panel is bound to.
  useEffect(() => {
    const onBind = (e) => setPanelBoundPos(e.detail?.pos ?? null);
    window.addEventListener('imagePropsPanelBind', onBind);
    return () => window.removeEventListener('imagePropsPanelBind', onBind);
  }, []);

  // Measure the image's layout size (unaffected by the rotation transform) so we
  // can reserve the rotated bounding box in the flow.
  useEffect(() => {
    const img = imageRef.current;
    if (img && img.offsetWidth) {
      setImgBox({ w: img.offsetWidth, h: img.offsetHeight });
    } else if (node.attrs.width && node.attrs.height) {
      setImgBox({ w: Number(node.attrs.width), h: Number(node.attrs.height) });
    }
  }, [node.attrs.width, node.attrs.height, node.attrs.rotation, displaySrc, isResizing]);

  useEffect(() => {
    if (!isResizing) return;

    const handleMouseMove = (e) => {
      if (!isResizing || !resizeDirection) return;
      // Kill any native text selection the drag is forming, every frame, so
      // ProseMirror's DOM observer never syncs it back into a TextSelection that
      // would deselect the image on mouseup (the real-mouse failure).
      e.preventDefault();
      const nativeSel = window.getSelection();
      if (nativeSel && nativeSel.rangeCount) nativeSel.removeAllRanges();

      const deltaX = e.clientX - startPosRef.current.x;
      const deltaY = e.clientY - startPosRef.current.y;

      let newWidth = startDimensionsRef.current.width;
      let newHeight = startDimensionsRef.current.height;

      // Calculate new dimensions based on resize direction
      if (resizeDirection.includes('right')) {
        newWidth = Math.max(50, Math.min(startDimensionsRef.current.width + deltaX, MAX_CONTENT_WIDTH));
      }
      if (resizeDirection.includes('left')) {
        newWidth = Math.max(50, Math.min(startDimensionsRef.current.width - deltaX, MAX_CONTENT_WIDTH));
      }
      if (resizeDirection.includes('bottom')) {
        newHeight = Math.max(50, Math.min(startDimensionsRef.current.height + deltaY, MAX_CONTENT_HEIGHT));
      }
      if (resizeDirection.includes('top')) {
        newHeight = Math.max(50, Math.min(startDimensionsRef.current.height - deltaY, MAX_CONTENT_HEIGHT));
      }

      // Update size AND re-assert the NodeSelection in the SAME transaction.
      // TipTap's updateAttributes drops the NodeSelection, which made the
      // properties panel (driven by a selection poll in EditorPage) close mid-drag
      // and never reopen. Keeping the selection on the node each step means the
      // poll always sees the image selected → panel stays open (T-042.img).
      const pos = typeof getPos === 'function' ? getPos() : null;
      if (pos != null && editor) {
        const { state, view } = editor;
        const node = state.doc.nodeAt(pos);
        if (node) {
          const tr = state.tr.setNodeMarkup(pos, undefined, {
            ...node.attrs,
            width: Math.round(newWidth),
            height: Math.round(newHeight),
          });
          tr.setSelection(NodeSelection.create(tr.doc, pos));
          view.dispatch(tr);
          return;
        }
      }
      // Fallback if we can't resolve the node position.
      updateAttributes({ width: Math.round(newWidth), height: Math.round(newHeight) });
    };

    const handleMouseUp = () => {
      setIsResizing(false);
      setResizeDirection(null);
      // Restore selection behaviour and drop any native text selection the drag
      // may have created, THEN re-assert the NodeSelection on the image so it
      // stays selected (border + handles + panel) after the resize. Guard the
      // node type so a stale pos can never select the wrong node.
      document.body.style.userSelect = '';
      document.body.style.webkitUserSelect = '';
      if (editor?.view?.dom) {
        editor.view.dom.style.userSelect = '';
        editor.view.dom.style.webkitUserSelect = '';
      }
      try {
        window.getSelection()?.removeAllRanges();
        const pos = typeof getPos === 'function' ? getPos() : null;
        if (pos != null && editor) {
          const n = editor.state.doc.nodeAt(pos);
          if (n && n.type.name === 'resizableImage') {
            editor.view.dispatch(
              editor.state.tr.setSelection(NodeSelection.create(editor.state.doc, pos))
            );
          }
        }
      } catch (e) { /* node removed mid-drag — nothing to reselect */ }
      finally { if (editor) editor.__imageResizing = false; }
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isResizing, resizeDirection, updateAttributes]);

  const rotation = node.attrs.rotation || 0;
  const width = node.attrs.width;
  const height = node.attrs.height;
  const opacity = node.attrs.opacity !== undefined ? node.attrs.opacity : 100;
  const { borderColor, borderWidth, borderStyle, borderRadius } = node.attrs;

  // T-042.img: The flow model lives on the NodeViewWrapper (the element that
  // actually sits in the paragraph). float-left/right float it and let text
  // wrap; inline keeps it in the text baseline; center/right align it via
  // margins. The "behind/overlay/front" layering modes were removed — an inline
  // atom cannot be reliably layered behind page text without a full
  // floating-layer feature, and the absolute-positioning hack made images
  // vanish behind the sheet background. Any legacy value degrades to 'inline'.
  const isFloat = wrapMode === 'float-left' || wrapMode === 'float-right';
  const isBlockAlign = !isFloat && (textAlign === 'center' || textAlign === 'right');

  const wrapperStyle = {
    // Float pulls the image out of the line so text wraps around it.
    ...(isFloat
      ? {
          float: wrapMode === 'float-left' ? 'left' : 'right',
          [wrapMode === 'float-left' ? 'marginRight' : 'marginLeft']: '16px',
          marginTop: '4px',
          marginBottom: '8px',
        }
      : {}),
    // center/right alignment: become a block and push with auto margins.
    ...(isBlockAlign
      ? {
          display: 'block',
          marginLeft: textAlign === 'right' ? 'auto' : 'auto',
          marginRight: textAlign === 'right' ? 0 : 'auto',
          width: 'fit-content',
        }
      : { display: 'inline-block', verticalAlign: 'middle' }),
    maxWidth: '100%',
    position: 'relative',
  };

  // The container ALWAYS wraps the image tightly: inline-block sizes to the img,
  // so it never collapses (the root cause of the float "thin blue line" and the
  // behind "height:0" bugs) and the selection ring always matches the image box.
  // Rotated bounding box of the image. We reserve the EXTRA space it needs via
  // margins (keeping the container in normal flow at its real size), so the text
  // reflows around a rotated image instead of it overlapping behind the text.
  const isRotated = Boolean(rotation) && rotation % 360 !== 0;
  const rad = ((rotation || 0) * Math.PI) / 180;
  const boxW = imgBox.w
    ? Math.abs(imgBox.w * Math.cos(rad)) + Math.abs(imgBox.h * Math.sin(rad))
    : 0;
  const boxH = imgBox.w
    ? Math.abs(imgBox.w * Math.sin(rad)) + Math.abs(imgBox.h * Math.cos(rad))
    : 0;
  const marginX = isRotated && boxW ? Math.max(0, Math.round((boxW - imgBox.w) / 2)) : 0;
  const marginY = isRotated && boxH ? Math.max(0, Math.round((boxH - imgBox.h) / 2)) : 0;

  const containerStyle = {
    position: 'relative',
    display: 'inline-block',
    userSelect: 'none',
    maxWidth: '100%',
    lineHeight: 0,
    // Rotate the container (image + handles turn together).
    transform: rotation ? `rotate(${rotation}deg)` : undefined,
    // Reserve the rotated bounding box so surrounding text makes room.
    margin: isRotated ? `${marginY}px ${marginX}px` : undefined,
  };

  const needsFloatClear = isFloat;

  // Show the selection chrome (border + resize handles + open-properties button)
  // when the node is selected, being resized, OR bound to the open properties
  // panel — so interacting with the panel never makes the handles vanish.
  const boundToPanel =
    panelBoundPos != null && typeof getPos === 'function' && getPos() === panelBoundPos;
  const showChrome = selected || isResizing || boundToPanel;

  // The image carries its comment as a NODE ATTRIBUTE (not a wrapping mark, which
  // leaked a 2px yellow border). When present (and not resolved) the resize
  // handles turn amber to flag a commented image.
  const hasComment = Boolean(node.attrs.commentId) && !node.attrs.commentResolved;

  // 10% bigger drag circles (12px → 13px). Shared base so the amber-when-commented
  // color and the size live in one place instead of on all 8 handles.
  const HANDLE = 13;
  const handleBase = {
    position: 'absolute',
    width: `${HANDLE}px`,
    height: `${HANDLE}px`,
    background: hasComment ? '#f59e0b' : '#0ea5e9',
    border: '2px solid white',
    borderRadius: '50%',
    zIndex: 10,
  };
  const handles = [
    { cls: 'nw', dir: 'top-left', cursor: 'nw-resize', pos: { top: '-4px', left: '-4px' } },
    { cls: 'ne', dir: 'top-right', cursor: 'ne-resize', pos: { top: '-4px', right: '-4px' } },
    { cls: 'sw', dir: 'bottom-left', cursor: 'sw-resize', pos: { bottom: '-4px', left: '-4px' } },
    { cls: 'se', dir: 'bottom-right', cursor: 'se-resize', pos: { bottom: '-4px', right: '-4px' } },
    { cls: 'n', dir: 'top', cursor: 'n-resize', pos: { top: '-4px', left: '50%', transform: 'translateX(-50%)' } },
    { cls: 's', dir: 'bottom', cursor: 's-resize', pos: { bottom: '-4px', left: '50%', transform: 'translateX(-50%)' } },
    { cls: 'w', dir: 'left', cursor: 'w-resize', pos: { top: '50%', left: '-4px', transform: 'translateY(-50%)' } },
    { cls: 'e', dir: 'right', cursor: 'e-resize', pos: { top: '50%', right: '-4px', transform: 'translateY(-50%)' } },
  ];

  return (
    <NodeViewWrapper
      className="resizable-image-wrapper"
      style={wrapperStyle}
    >
      <div
        ref={containerRef}
        className={`resizable-image-container ${showChrome ? 'selected' : ''}`}
        style={containerStyle}
        data-wrap={wrapMode}
        {...(node.attrs.commentId ? { 'data-comment-id': node.attrs.commentId } : {})}
        {...(node.attrs.commentResolved ? { 'data-resolved': 'true' } : {})}
      >
        {displaySrc ? (
          <img
            ref={imageRef}
            src={displaySrc}
            alt={node.attrs.alt || ''}
            className="editor-image"
            loading="lazy"
            decoding="async"
            data-wrap={node.attrs.wrap}
            data-rotation={rotation}
            width={width}
            height={height}
            style={{
              // Rotation lives on the container now (so the handles rotate too).
              // Opacity lives on the image ONLY, so the handles/button stay solid.
              opacity: opacity / 100,
              maxWidth: '100%',
              display: 'block',
              border: borderWidth ? `${borderWidth}px ${borderStyle || 'solid'} ${borderColor || '#000000'}` : 'none',
              borderRadius: borderRadius ? `${borderRadius}px` : '0',
              // Light backing until the pixels arrive → no black flash on the dark
              // paper while the signed CDN url loads. Removed once painted.
              backgroundColor: imgPainted ? undefined : '#f1f5f9',
            }}
            draggable={false}
            onLoad={() => setImgPainted(true)}
            onError={handleImgError}
            onDoubleClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              window.dispatchEvent(new CustomEvent('openImageLightbox', {
                detail: { src: rawSrc, alt: node.attrs.alt || '' },
              }));
            }}
          />
        ) : (
          <div
            className="editor-image editor-image-loading"
            style={{
              width: width || 300,
              height: height || 200,
              maxWidth: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: '#f1f5f9',
              color: '#94a3b8',
              fontSize: '12px',
              borderRadius: borderRadius ? `${borderRadius}px` : '4px',
            }}
          >
            Cargando imagen…
          </div>
        )}

        {/* Resize handles: show while selected AND while resizing, so a real
            drag doesn't blink the handles out when ProseMirror transiently drops
            the NodeSelection during updateAttributes (T-042.img). */}
        {showChrome && handles.map(({ cls, dir, cursor, pos }) => (
          <div
            key={cls}
            className={`resize-handle resize-handle-${cls}`}
            data-resize-dir={dir}
            style={{ ...handleBase, cursor, ...pos }}
          />
        ))}

        {/* The "open image properties" button now floats below the AI quick-action
            button (rendered by EditorPage in the scroll container), not on the
            image itself. See FloatingImagePropsButton. */}
      </div>
      {needsFloatClear && (
        <div
          className="resizable-image-clear"
          style={{ clear: wrapMode === 'float-left' ? 'left' : 'right' }}
        />
      )}
    </NodeViewWrapper>
  );
};

// TipTap extension
export const ResizableImage = Node.create({
  name: 'resizableImage',

  group: 'inline',

  inline: true,

  atom: true,

  // T-042.img: draggable:true (+ data-drag-handle) made the whole image a drag
  // source, and the browser swallowed the click that selects the atom — no
  // border/handles/panel. Disabled so click-to-select is deterministic.
  draggable: false,

  addAttributes() {
    return {
      src: {
        default: null,
        parseHTML: element => element.getAttribute('src'),
        renderHTML: attributes => {
          if (!attributes.src) return {};
          return { src: attributes.src };
        },
      },
      alt: {
        default: null,
        parseHTML: element => element.getAttribute('alt'),
        renderHTML: attributes => {
          if (!attributes.alt) return {};
          return { alt: attributes.alt };
        },
      },
      title: {
        default: null,
        parseHTML: element => element.getAttribute('title'),
        renderHTML: attributes => {
          if (!attributes.title) return {};
          return { title: attributes.title };
        },
      },
      width: {
        default: null,
        parseHTML: element => {
          const width = element.getAttribute('width');
          return width ? parseInt(width) : null;
        },
        renderHTML: attributes => {
          if (!attributes.width) return {};
          return { width: attributes.width };
        },
      },
      height: {
        default: null,
        parseHTML: element => {
          const height = element.getAttribute('height');
          return height ? parseInt(height) : null;
        },
        renderHTML: attributes => {
          if (!attributes.height) return {};
          return { height: attributes.height };
        },
      },
      rotation: {
        default: 0,
        parseHTML: element => parseInt(element.getAttribute('data-rotation') || '0'),
        renderHTML: attributes => {
          return {
            'data-rotation': attributes.rotation,
          };
        },
      },
      borderColor: {
        default: null,
        parseHTML: element => element.style.borderColor,
        renderHTML: attributes => {
          if (!attributes.borderColor) return {};
          return { style: `border-color: ${attributes.borderColor}` };
        },
      },
      borderWidth: {
        default: 0,
        parseHTML: element => parseInt(element.style.borderWidth) || 0,
        renderHTML: attributes => {
          if (!attributes.borderWidth) return {};
          return { style: `border-width: ${attributes.borderWidth}px` };
        },
      },
      borderStyle: {
        default: 'solid',
        parseHTML: element => element.style.borderStyle,
        renderHTML: attributes => {
          if (!attributes.borderStyle) return {};
          return { style: `border-style: ${attributes.borderStyle}` };
        },
      },
      borderRadius: {
        default: 0,
        parseHTML: element => parseInt(element.style.borderRadius) || 0,
        renderHTML: attributes => {
          if (!attributes.borderRadius) return {};
          return { style: `border-radius: ${attributes.borderRadius}px` };
        },
      },
      wrap: {
        default: 'inline',
        parseHTML: element => {
          // Normalise legacy values from documents saved before the wrap
          // vocabulary was unified (old WrapSection: none/left/right/square).
          const raw = element.getAttribute('data-wrap') || 'inline';
          const legacy = {
            none: 'inline',
            left: 'float-left',
            right: 'float-right',
            square: 'float-left',
            // Removed layering modes degrade to inline (T-042.img).
            behind: 'inline',
            overlay: 'inline',
            front: 'inline',
          };
          return legacy[raw] || raw;
        },
        renderHTML: attributes => {
          return {
            'data-wrap': attributes.wrap,
          };
        },
      },
      textAlign: {
        default: 'left',
        parseHTML: element => element.getAttribute('data-text-align') || element.style.textAlign || 'left',
        renderHTML: attributes => {
          const align = attributes.textAlign || 'left';
          return {
            'data-text-align': align,
            style: `text-align: ${align};`,
          };
        },
      },
      opacity: {
        default: 100,
        parseHTML: element => parseInt(element.getAttribute('data-opacity') || '100'),
        renderHTML: attributes => {
          return {
            'data-opacity': attributes.opacity || 100,
          };
        },
      },
      // Comment anchor: the image stores its commentId directly (not a wrapping
      // mark). Persisted so a commented image survives save/reload.
      commentId: {
        default: null,
        parseHTML: element => element.getAttribute('data-comment-id'),
        renderHTML: attributes => (attributes.commentId ? { 'data-comment-id': attributes.commentId } : {}),
      },
      commentResolved: {
        default: false,
        parseHTML: element => element.getAttribute('data-comment-resolved') === 'true',
        renderHTML: attributes => (attributes.commentResolved ? { 'data-comment-resolved': 'true' } : {}),
      },
      // AI vision description saved onto the image (paid once, reused as chat
      // context). Persisted so it survives editor re-serialization save/reload.
      imageDescription: {
        default: null,
        parseHTML: element => element.getAttribute('data-description') || null,
        renderHTML: attributes => (attributes.imageDescription ? { 'data-description': attributes.imageDescription } : {}),
      },
    };
  },

  parseHTML() {
    return [
      {
        tag: 'img[src]',
      },
    ];
  },

  renderHTML({ HTMLAttributes }) {
    const { textAlign, style, ...rest } = HTMLAttributes;
    const alignAttr = HTMLAttributes['data-text-align'] || textAlign;
    const wrapAttr = HTMLAttributes['data-wrap'] || 'inline';

    const alignStyles = () => {
      switch (alignAttr) {
        case 'center':
          return 'display:block;margin-left:auto;margin-right:auto;';
        case 'right':
          return 'display:block;margin-left:auto;margin-right:0;';
        default:
          return 'display:block;margin-left:0;margin-right:auto;';
      }
    };

    const wrapModeStyles = () => {
      switch (wrapAttr) {
        case 'float-left':
          return 'float:left;margin-right:16px;margin-bottom:8px;';
        case 'float-right':
          return 'float:right;margin-left:16px;margin-bottom:8px;';
        // behind/overlay/front were removed (T-042.img): they degrade to inline
        // on export so old documents keep the image visible instead of hidden.
        case 'inline':
        default:
          return 'display:inline-block;vertical-align:middle;';
      }
    };

    const borderStyles = () => {
      const { borderWidth, borderStyle, borderColor, borderRadius } = HTMLAttributes;
      let styles = '';
      if (borderWidth) styles += `border-width:${borderWidth}px;`;
      if (borderStyle) styles += `border-style:${borderStyle};`;
      if (borderColor) styles += `border-color:${borderColor};`;
      if (borderRadius) styles += `border-radius:${borderRadius}px;`;
      return styles;
    };

    // Persist opacity so it survives export + reload. Emit only when it differs
    // from the default (100). Blend mode was removed (T-042.img).
    const opacityStyles = () => {
      const rawOpacity = HTMLAttributes['data-opacity'];
      const opacityVal = rawOpacity === undefined || rawOpacity === null
        ? 100
        : parseInt(rawOpacity, 10);
      if (!Number.isNaN(opacityVal) && opacityVal !== 100) {
        return `opacity:${opacityVal / 100};`;
      }
      return '';
    };

    const mergedStyle = style
      ? `${style};${alignStyles()};${wrapModeStyles()};${borderStyles()}${opacityStyles()}`
      : `${alignStyles()}${wrapModeStyles()}${borderStyles()}${opacityStyles()}`;

    return [
      'img',
      mergeAttributes(rest, {
        style: mergedStyle,
        'data-text-align': alignAttr || 'left',
        'data-wrap': wrapAttr,
      }),
    ];
  },

  addNodeView() {
    return ReactNodeViewRenderer(ResizableImageComponent);
  },

  addCommands() {
    return {
      setResizableImage:
        (options) =>
        ({ commands, editor }) => {
          const { state } = editor;
          const { $from } = state.selection;
          const parentIsTextblock = $from.parent?.isTextblock;

          if (!parentIsTextblock) {
            return commands.insertContent({
              type: 'paragraph',
              content: [
                {
                  type: this.name,
                  attrs: options,
                },
              ],
            });
          }

          return commands.insertContent({
            type: this.name,
            attrs: options,
          });
        },
    };
  },
});

export default ResizableImage;
